import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL MOMENTUM - KAM (Key Account Manager) Visits API
// -----------------------------------------------------------------------------
// KAM is the HQ / account-level visit flow. Steps 1–4 mirror the regular store
// visit UI but pull KAM-specific data, so they live in this separate slice.
//
// NOTE (placeholders): the requestType values here are placeholders (GET_KAM_*).
// Replace them with the real backend request types once available.
//
// NOTE (identifier): the flow currently reuses the store-style identifier
// (`?store=` -> store_user_uid) per the agreed interim. When the account-level
// identifier is defined, swap `store_user_uid: params.storeUserUid` for the
// account/agency uid here.
// =============================================================================

export const royalMomentumKamVisitsApi = createApi({
    reducerPath: "royalMomentumKamVisitsApi",
    baseQuery: apiRequestBaseQuery,
    // "KamVisitDetail" covers one visit's own saved answers (step data / progress / summary); it
    // is kept apart from "KamVisits" so a step save inside the visit flow does not refetch the
    // whole upcoming/past visit lists.
    tagTypes: ["KamVisitStats", "KamVisits", "KamVisitDetail", "KamAccounts"],
    endpoints: (builder) => ({
        getKamTotalBookings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreTotalBookingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamRevenueThisYearFiltered: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreRevenueThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamTotalPassengers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreTotalPassengersV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamCustomerYearlyTrips: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreCustomerYearlyTripsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        year: params.year || new Date().getFullYear().toString(),
                    },
                };
            },
        }),

        getKamBookingsAreSailing: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingsAreSailingV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamCustomersCabinTypes: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreCustomersCabinTypesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamTopCruises: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreTopCruisesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        limit: params.limit || 10,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamBookingOverview: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingOverviewV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                        page: params.page || 1,
                        limit: params.limit || 10,
                    },
                };
            },
        }),

        getKamBookingToDeparture: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingToDepartureV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        year: params.year || new Date().getFullYear().toString(),
                    },
                };
            },
        }),

        getKamShipMix: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreShipMixV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamTotalProposalsCreated: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreTotalProposalsCreatedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamUniqueAgentsUsedGenie: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreUniqueAgentsUsedGenieV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        type: params.type || "year",
                        start_date: params.startDate || new Date().getFullYear().toString(),
                        ...(params.endDate && { end_date: params.endDate }),
                    },
                };
            },
        }),

        getKamAgentsEnrolledUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreAgentsEnrolledUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamBookingsClaimedUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingsClaimedUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamBookingsClaimedThroughUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingsClaimedThroughUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamBookingsClaimViaUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingsClaimViaUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamModulesCompleted: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreModulesCompletedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamRoyalGurus: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreRoyalGurusV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamRoyalGurusList: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreRoyalGurusListV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamFocusModules: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreFocusModulesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                        year: params.year || "2026",
                    },
                };
            },
        }),

        getKamLastVisit: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreLastVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamRevenueThisYear: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreRevenueThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamRevenueSinceLastVisit: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreRevenueSinceLastVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamPaxThisYear: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStorePaxThisYearV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamProductMix: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreProductMixV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamClubRoyalsMembers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getClubRoyalsMembersV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamMyRoyalGenieMembers: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getMyRoyalGenieMembersV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamProposalsGenerated: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreProposalsGeneratedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamBookingsRedeemedUpperDeck: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreBookingsRedeemedUpperDeckV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamClubLearnModulesCompleted: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreClubLearnModulesCompletedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        getKamWowBookings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (KAM account identifier) replaces travel_agency_uid
                    requestType: requestType.getStoreWowBookingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.storeUserUid,
                    },
                };
            },
        }),

        addKamVisit: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.addKamVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_name: params.visitName,
                        visit_type: params.visitType,
                        bullet_points: params.bulletPoints || [],
                        internal_attendee_uids: params.internalAttendeeUids || "",
                        external_attendee_uids: params.externalAttendeeUids || "",
                        visit_date: params.visitDate,
                        visit_time: params.visitTime,
                    },
                };
            },
            invalidatesTags: ["KamVisits"],
        }),

        // Update KAM Visit — the step-6 "complete visit" write (is_visited: 1) and any other edit
        // to a booked visit. Defined here rather than called as a raw apiRequest so that finishing
        // a visit moves it out of the upcoming lists immediately.
        updateKamVisit: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.updateKamVisit,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        kam_visit_id: params.kamVisitId,
                        ...(params.isVisited !== undefined && { is_visited: params.isVisited }),
                        ...(params.visitDate && { visit_date: params.visitDate }),
                        ...(params.visitTime && { visit_time: params.visitTime }),
                        ...(params.visitType && { visit_type: params.visitType }),
                    },
                };
            },
            invalidatesTags: ["KamVisits", "KamVisitStats", "KamVisitDetail"],
        }),

        getKamVisitProgress: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getKamVisitProgress,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        kam_visit_id: params.kamVisitId,
                    },
                };
            },
        }),

        setKamVisitProgress: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.setKamVisitProgress,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        kam_visit_id: params.kamVisitId,
                        step: params.step,
                    },
                };
            },
            invalidatesTags: ["KamVisitDetail"],
        }),

        setKamVisitStepData: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.setKamVisitStepData,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        kam_visit_id: params.kamVisitId,
                        step: params.step,
                        fields: params.fields,
                    },
                };
            },
            invalidatesTags: ["KamVisitDetail"],
        }),

        getKamVisitStepData: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getKamVisitStepData,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        kam_visit_id: params.kamVisitId,
                        step: params.step,
                    },
                };
            },
        }),

        getKamNextSteps: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getKamNextStepsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
        }),

        getKamVisit: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: same kam_visit_id payload (no agency key); requestType bumped to V2
                    requestType: requestType.getKamVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        kam_visit_id: params.kamVisitId,
                    },
                };
            },
            providesTags: ["KamVisitDetail"],
            keepUnusedDataFor: 0,
        }),

        getKamVisitPublic: builder.query({
            query: (params) => ({
                // V2: same kam_visit_id payload (no agency key); requestType bumped to V2
                requestType: requestType.getKamVisitPublicV2,
                data: {
                    kam_visit_id: params.kamVisitId,
                },
            }),
        }),

        // The agency's KAM visit list, read right after booking one (the book-a-visit flow
        // navigates straight into visit-summary). Untagged it would serve a list captured before
        // the booking, so the visit the user just created would be missing.
        getAgencyKamVisit: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) replaces travel_agency_uid
                    requestType: requestType.getAgencyKamVisitV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                    },
                };
            },
            providesTags: ["KamVisits"],
            keepUnusedDataFor: 0,
        }),

        getKamVisitSummary: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getKamVisitSummary,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        store_visit_id: params.storeVisitId,
                    },
                };
            },
            providesTags: ["KamVisitDetail"],
            keepUnusedDataFor: 0,
        }),

        getKamVisits: builder.query({
            query: (params = {}) => {
                const userDetails = getUserDetails();
                return {
                    // V2: agency_id (= selectedBrand.uid = agency_id_no_r) is an optional extra
                    // filter here; `user_uid` still scopes the list to visits this KAM created, so
                    // omitting agency_id keeps the existing behaviour rather than widening it.
                    // Note the V2 filter is an exact match — it is NOT expanded to the affiliate
                    // group, because a visit belongs to one physical office.
                    requestType: requestType.getKamVisitsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        show: params.show || "upcoming",
                        page: params.page || 1,
                        limit: params.limit || 50,
                        ...(params.type && { type: params.type }),
                        ...(params.date && { date: params.date }),
                        ...(params.search && { search: params.search }),
                        ...(params.travelAgencyUid && { agency_id: params.travelAgencyUid }),
                    },
                };
            },
            providesTags: ["KamVisits"],
            // Booked visits are user-created records, so a stale list is always wrong. Dropping
            // the entry on unsubscribe makes every remount refetch.
            keepUnusedDataFor: 0,
        }),

        // The KAM Overview's two stat endpoints. It used to borrow the store-side
        // `getTotalStoreVisits` / `getStoreVisitImpact`, which count store visits — so a KAM saw
        // store-visit figures on a page about KAM visits. These are the KAM-scoped equivalents;
        // the payload is identical, only the requestType differs.
        getTotalKamVisits: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalKamVisitsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        visit_status: params.visitStatus ?? "",
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["KamVisitStats"],
        }),

        getKamVisitImpact: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getKamVisitImpactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["KamVisitStats"],
        }),

        // Accounts with no visit in the period. The KAM-scoped counterpart to the store side's
        // `getTotalStoresNotVisited` (GET_TOTAL_STORES_NOT_VISITED_V2), which the KAM Overview used
        // to borrow — same payload, only the requestType differs.
        getTotalKamNotVisited: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getTotalKamNotVisitedV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid,
                        type: params.type || "year",
                        date: params.date || new Date().getFullYear().toString(),
                    },
                };
            },
            providesTags: ["KamVisitStats"],
        }),
    }),
});

export const {
    useGetKamTotalBookingsQuery,
    useGetKamRevenueThisYearFilteredQuery,
    useGetKamTotalPassengersQuery,
    useGetKamCustomerYearlyTripsQuery,
    useGetKamBookingsAreSailingQuery,
    useGetKamCustomersCabinTypesQuery,
    useGetKamTopCruisesQuery,
    useGetKamBookingOverviewQuery,
    useGetKamBookingToDepartureQuery,
    useGetKamShipMixQuery,
    useGetKamTotalProposalsCreatedQuery,
    useGetKamUniqueAgentsUsedGenieQuery,
    useGetKamAgentsEnrolledUpperDeckQuery,
    useGetKamBookingsClaimedUpperDeckQuery,
    useGetKamBookingsClaimedThroughUpperDeckQuery,
    useGetKamBookingsClaimViaUpperDeckQuery,
    useGetKamModulesCompletedQuery,
    useGetKamRoyalGurusQuery,
    useGetKamRoyalGurusListQuery,
    useGetKamFocusModulesQuery,
    useGetKamLastVisitQuery,
    useGetKamRevenueThisYearQuery,
    useGetKamRevenueSinceLastVisitQuery,
    useGetKamPaxThisYearQuery,
    useGetKamProductMixQuery,
    useGetKamClubRoyalsMembersQuery,
    useGetKamMyRoyalGenieMembersQuery,
    useGetKamProposalsGeneratedQuery,
    useGetKamBookingsRedeemedUpperDeckQuery,
    useGetKamClubLearnModulesCompletedQuery,
    useGetKamWowBookingsQuery,
    useAddKamVisitMutation,
    useUpdateKamVisitMutation,
    useGetKamVisitProgressMutation,
    useSetKamVisitProgressMutation,
    useSetKamVisitStepDataMutation,
    useGetKamVisitStepDataMutation,
    useGetKamNextStepsMutation,
    useGetKamVisitQuery,
    useGetKamVisitPublicQuery,
    useGetAgencyKamVisitQuery,
    useGetKamVisitSummaryQuery,
    useGetKamVisitsQuery,
    useGetTotalKamVisitsQuery,
    useGetKamVisitImpactQuery,
    useGetTotalKamNotVisitedQuery,
} = royalMomentumKamVisitsApi;
